import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { API } from "../../data/API/API";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import "./InternalCommentsModal.scss";

type TargetType = "review" | "sending" | "review-phone-review";

type Props = {
  show: boolean;
  onClose: () => void;
  targetType: TargetType;
  targetId: number;
  title?: string;
  onCreated?: () => void;
};

const InternalCommentsModal: React.FC<Props> = ({
  show,
  onClose,
  targetType,
  targetId,
  title,
  onCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [text, setText] = useState("");

  // Inline edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  // Confirm modals state
  const [showConfirmSaveEdit, setShowConfirmSaveEdit] = useState(false);
  const [pendingEditId, setPendingEditId] = useState<number | null>(null);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  // Busy state for actions
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // const headerTitle = useMemo(() => {
  //   if (title) return title;
  //   return targetType === "review"
  //     ? `Interna kommentarer (Granskning ${targetId})`
  //     : `Interna kommentarer (Utskick ${targetId})`;
  // }, [title, targetType, targetId]);
const headerTitle = useMemo(() => {
  if (title) return title;

  if (targetType === "review") {
    return `Interna kommentarer (Granskning ${targetId})`;
  }

  if (targetType === "sending") {
    return `Interna kommentarer (Utskick ${targetId})`;
  }

  return `Interna kommentarer (Telefongranskning ${targetId})`;
}, [title, targetType, targetId]);
  // const fetchComments = async () => {
  //   setLoading(true);
  //   try {
  //     const data =
  //       targetType === "review"
  //         ? await API.InternalComments.getForReview(targetId)
  //         : await API.InternalComments.getForSending(targetId);
  //     setComments(data);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchComments = async () => {
  setLoading(true);
  try {
    const data =
      targetType === "review"
        ? await API.InternalComments.getForReview(targetId)
        : targetType === "sending"
        ? await API.InternalComments.getForSending(targetId)
        : await API.InternalComments.getForReviewPhoneReview(targetId);

    setComments(data ?? []);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (show) {
      // Reset state när modalen öppnas
      setText("");
      setEditingId(null);
      setEditText("");
      setShowConfirmSaveEdit(false);
      setPendingEditId(null);
      setShowConfirmDelete(false);
      setPendingDeleteId(null);
      setUpdatingId(null);
      setDeletingId(null);

      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, targetType, targetId]);

  // const handleCreate = async () => {
  //   const trimmed = text.trim();
  //   if (!trimmed) return;

  //   setSaving(true);
  //   try {
  //     const created =
  //       targetType === "review"
  //         ? await API.InternalComments.createForReview(targetId, trimmed)
  //         : await API.InternalComments.createForSending(targetId, trimmed);

  //     if (created) {
  //       setComments((prev) => [created, ...prev]); // nyast först
  //       setText("");
  //       onCreated?.();
  //     }
  //   } finally {
  //     setSaving(false);
  //   }
  // };
  const handleCreate = async () => {
  const trimmed = text.trim();
  if (!trimmed) return;

  setSaving(true);
  try {
    const created =
      targetType === "review"
        ? await API.InternalComments.createForReview(targetId, trimmed)
        : targetType === "sending"
        ? await API.InternalComments.createForSending(targetId, trimmed)
        : await API.InternalComments.createForReviewPhoneReview(targetId, trimmed);

    if (created) {
      setComments((prev) => [created, ...prev]); // nyast först
      setText("");
      onCreated?.();
    }
  } finally {
    setSaving(false);
  }
};

  const startEdit = (commentId: number, currentText: string) => {
    setEditingId(commentId);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  // Klick på “check”-ikonen => öppna confirm
  const requestSaveEdit = (commentId: number) => {
    if (!editText.trim()) return;
    setPendingEditId(commentId);
    setShowConfirmSaveEdit(true);
  };

  const confirmSaveEdit = async () => {
    if (pendingEditId == null) return;
    const commentId = pendingEditId;
    const trimmed = editText.trim();
    if (!trimmed) return;

    setUpdatingId(commentId);
    try {
      const updated = await API.InternalComments.update(commentId, trimmed);
      if (updated) {
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...c, text: updated.text } : c))
        );
        setEditingId(null);
        setEditText("");
      }
    } finally {
      setUpdatingId(null);
      setShowConfirmSaveEdit(false);
      setPendingEditId(null);
    }
  };

  // Klick på “trash”-ikonen => öppna confirm
  const requestDelete = (commentId: number) => {
    setPendingDeleteId(commentId);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    const commentId = pendingDeleteId;

    setDeletingId(commentId);
    try {
      const deleted = await API.InternalComments.remove(commentId);
      if (deleted) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));

        // Om man råkade ta bort den man editerar
        if (editingId === commentId) {
          setEditingId(null);
          setEditText("");
        }
      }
    } finally {
      setDeletingId(null);
      setShowConfirmDelete(false);
      setPendingDeleteId(null);
    }
  };

  const isBusy = (id: number) => updatingId === id || deletingId === id;

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{headerTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <>
              {/* Create */}
              <div style={{ marginBottom: 12 }}>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Skriv en intern kommentar..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={saving}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button className="btn" onClick={handleCreate} disabled={saving || !text.trim()}>
                    {saving ? "Sparar..." : "Spara kommentar"}
                  </button>
                </div>
              </div>

              {/* List */}
              {comments.length === 0 ? (
                <p>Inga interna kommentarer ännu.</p>
              ) : (
                <div className="internal-comments-list">
                  {comments.map((c) => {
                    const d = new Date(c.created);
                    const createdStr = isNaN(d.getTime()) ? c.created : format(d, "yyyy-MM-dd HH:mm");
                    const isEditing = editingId === c.id;

                    return (
                      <div key={c.id} className="internal-comment">
                        {/* Actions top-right */}
                        <div className="internal-comment-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="icon-btn"
                                title="Avbryt"
                                onClick={cancelEdit}
                                disabled={isBusy(c.id)}
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                              <button
                                className="icon-btn"
                                title="Spara"
                                onClick={() => requestSaveEdit(c.id)}
                                disabled={isBusy(c.id) || !editText.trim()}
                              >
                                <FontAwesomeIcon icon={faCheck} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="icon-btn"
                                title="Redigera"
                                onClick={() => startEdit(c.id, c.text)}
                                disabled={isBusy(c.id)}
                              >
                                <FontAwesomeIcon icon={faPen} />
                              </button>
                              <button
                                className="icon-btn"
                                title="Ta bort"
                                onClick={() => requestDelete(c.id)}
                                disabled={isBusy(c.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </>
                          )}
                        </div>

                        <div className="meta">
                          <b>{c.createdBy}</b>
                          <span className="date">{createdStr}</span>
                        </div>

                        {isEditing ? (
                          <textarea
                            className="form-control"
                            rows={3}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            disabled={isBusy(c.id)}
                          />
                        ) : (
                          <div className="text">{c.text}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button className="btn button-cancel" onClick={onClose}>
            Stäng
          </button>
        </Modal.Footer>
      </Modal>

      {/* Confirm modal: Save edit */}
      <Modal show={showConfirmSaveEdit} centered onHide={() => setShowConfirmSaveEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bekräfta ändring</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Är du säker på att du vill spara ändringen?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn button-cancel"
            onClick={() => {
              setShowConfirmSaveEdit(false);
              setPendingEditId(null);
            }}
            disabled={pendingEditId != null && updatingId === pendingEditId}
          >
            Avbryt
          </button>
          <button
            className="btn"
            onClick={confirmSaveEdit}
            disabled={pendingEditId == null || (pendingEditId != null && updatingId === pendingEditId)}
          >
            {pendingEditId != null && updatingId === pendingEditId ? "Sparar..." : "Bekräfta"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Confirm modal: Delete */}
      <Modal show={showConfirmDelete} centered onHide={() => setShowConfirmDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Bekräfta borttagning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Är du säker på att du vill ta bort kommentaren?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn button-cancel"
            onClick={() => {
              setShowConfirmDelete(false);
              setPendingDeleteId(null);
            }}
            disabled={pendingDeleteId != null && deletingId === pendingDeleteId}
          >
            Avbryt
          </button>
          <button
            className="btn"
            onClick={confirmDelete}
            disabled={pendingDeleteId == null || (pendingDeleteId != null && deletingId === pendingDeleteId)}
          >
            {pendingDeleteId != null && deletingId === pendingDeleteId ? "Tar bort..." : "Bekräfta"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InternalCommentsModal;
